import { Component, inject, input, InputSignal, signal, ViewEncapsulation, WritableSignal } from '@angular/core';

import { MD_DIALOG_REF, MDDialogModule, MDDialogRef, MDIconComponent } from '@melodic-ui/components';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LoaderComponent } from '../loader/loader.component';

@Component({
	selector: 'lb-confirmation-dialog',
	standalone: true,
	imports: [MDIconComponent, MDDialogModule, LoaderComponent],
	templateUrl: './confirmation-dialog.component.html',
	styleUrl: './confirmation-dialog.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class ConfirmationDialogComponent {
	private dialogRef: MDDialogRef = inject(MD_DIALOG_REF);

	//Required
	public title: InputSignal<string> = input.required<string>();
	public body: InputSignal<string> = input.required<string>();

	//Optional
	public icon: InputSignal<string> = input<string>('untitled-ui/general/check-done-02');
	public successText: InputSignal<string> = input<string>('Submit');
	public action: InputSignal<(() => Observable<any>) | undefined> = input<(() => Observable<any>) | undefined>();

	public isLoading: WritableSignal<boolean> = signal(false);
	public errorMessage: WritableSignal<string | null> = signal(null);

	close(success: boolean): void {
		this.dialogRef.close(success);
	}

	async confirm(): Promise<void> {
		const actionFn = this.action();

		if (actionFn) {
			this.isLoading.set(true);
			this.errorMessage.set(null); // Clear any previous errors

			actionFn()
				.pipe(
					catchError((error) => {
						console.error('Action failed:', error);
						// Extract error message from different possible error structures
						let errorMsg = 'An unexpected error occurred. Please try again.';
						if (error?.error?.message) {
							errorMsg = error.error.message;
						} else if (error?.message) {
							errorMsg = error.message;
						} else if (typeof error === 'string') {
							errorMsg = error;
						}
						this.errorMessage.set(errorMsg);
						return of(false);
					}),
					finalize(() => {
						this.isLoading.set(false);
					})
				)
				.subscribe({
					next: (result) => {
						if (result !== false) {
							this.dialogRef.close(true);
						}
					},
					error: () => {
						// Error already handled in catchError
					}
				});
		} else {
			this.close(true);
		}
	}
}
